/** ID管理の画面で共通に使う問い合わせと小道具。 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { useCallback } from "react";

import { useApi, useMe } from "../../lib/useApi";
import { hasRole, isAnyAdmin } from "../../lib/roles";
import { BULK_POLL_MS, BULK_RUNNING, roleOptions, scopeOptions } from "./userOptions";

/** ロールの選択肢(操作者が配れるものだけ。規則は `server/app/idp/roles.py`)。 */
export function useRoleOptions() {
  const me = useMe();
  return roleOptions(me.data);
}

/** API クライアントのスコープの選択肢(`suite:admin` は出ない)。 */
export function useScopeOptions() {
  const me = useMe();
  return scopeOptions(me.data);
}

/** 書き込みの可否。ボタンの出し分けに使う(権限の真実は API 側)。 */
export function useIdpPerms() {
  const me = useMe();
  return {
    canView: hasRole(me.data, "idp:viewer"),
    canRun: hasRole(me.data, "idp:operator"),
    canAdmin: hasRole(me.data, "idp:admin"),
  };
}

/** どれかのアプリの admin か(管理者操作ログのタブの出し分け。R7c fix1 低1)。 */
export function useAnyAdmin(): boolean {
  const me = useMe();
  return isAnyAdmin(me.data);
}

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/**
 * 最新の突合。1度も走っていなければ null(API は 204)。
 * react-query v5 は queryFn が undefined を返すとエラー扱いにするので、null に倒す。
 */
export function useReconciliation(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "reconciliation"],
    queryFn: async () => (await api.reconciliation()) ?? null,
    enabled,
  });
}

export function useReconcileRuns(params: { limit?: number; offset?: number; enabled?: boolean }) {
  const api = useApi();
  const search = new URLSearchParams({
    limit: String(params.limit ?? 20),
    offset: String(params.offset ?? 0),
  });
  return useQuery({
    queryKey: ["idp", "runs", params.limit ?? 20, params.offset ?? 0],
    queryFn: () => api.reconcileRuns(search),
    enabled: params.enabled ?? true,
  });
}

export function useReconcileRun(id: number | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "run", id],
    queryFn: () => api.reconcileRun(id!),
    enabled: !!id,
  });
}

export function useIdpConnectors(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "connectors"],
    queryFn: api.idpConnectors,
    enabled,
  });
}

/** `gws` / `keycloak` / `reconcile`。読めるのは idp:admin だけ。 */
export function useIdpSetting(key: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "setting", key],
    queryFn: () => api.idpSetting(key),
    enabled,
  });
}

/** 突合・設定が動いたら idp と通知・監査のキャッシュをまとめて捨てる。 */
export function useInvalidateIdp() {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["idp"] });
    void qc.invalidateQueries({ queryKey: ["notifications"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
  }, [qc]);
}

// ---- サービスプロバイダー(R7a)-------------------------------------------------

/** 一覧。`q` は表示名・サービス名の部分一致、`kind` は種別の絞り込み。 */
export function useProviders(params: {
  q?: string;
  kind?: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) {
  const api = useApi();
  const search = new URLSearchParams({
    limit: String(params.limit ?? 50),
    offset: String(params.offset ?? 0),
    ...(params.q ? { q: params.q } : {}),
    ...(params.kind ? { kind: params.kind } : {}),
  });
  return useQuery({
    queryKey: ["idp", "providers", params.q ?? "", params.kind ?? "", params.limit ?? 50,
      params.offset ?? 0],
    queryFn: () => api.providers(search),
    enabled: params.enabled ?? true,
  });
}

export function useProvider(id: number | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "provider", id],
    queryFn: () => api.provider(id!),
    enabled: !!id,
  });
}

/** IdP 情報(SP に配る値)。`id` を渡すとダイレクトログイン URL も入る。 */
export function useIdpInfo(id?: number | null, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "idp-info", id ?? 0],
    queryFn: () => api.idpInfo(id ?? undefined),
    enabled,
  });
}

/** ファイルを落とす(Authorization が要るので fetch して blob にする)。 */
export function useIdpDownload() {
  const api = useApi();
  return useCallback(
    async (path: string) => {
      try {
        const { blob, filename } = await api.download(path);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (e) {
        void message.error(errorText(e));
      }
    },
    [api],
  );
}

// ---- ユーザー管理・ドメイン設定・API クライアント(R7b)-------------------------

/** ユーザー一覧。`q` はユーザー名・氏名・メールの部分一致、`enabled` は "true"/"false"。 */
export function useIdpUsers(params: {
  q?: string;
  enabled?: string;
  limit?: number;
  offset?: number;
}) {
  const api = useApi();
  const search = new URLSearchParams({
    limit: String(params.limit ?? 50),
    offset: String(params.offset ?? 0),
    ...(params.q ? { q: params.q } : {}),
    ...(params.enabled ? { enabled: params.enabled } : {}),
  });
  return useQuery({
    queryKey: ["idp", "users", params.q ?? "", params.enabled ?? "", params.limit ?? 50,
      params.offset ?? 0],
    queryFn: () => api.idpUsers(search),
  });
}

export function useIdpUser(id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "user", id],
    queryFn: () => api.idpUser(id!),
    enabled: !!id,
  });
}

/** 宣言済みのカスタム属性(ユーザーのフォームの欄にもなる)。 */
export function useIdpCustomAttrs(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "custom-attrs"],
    queryFn: api.idpCustomAttrs,
    enabled,
  });
}

export function useIdpBulkJobs(params: { limit?: number; offset?: number }) {
  const api = useApi();
  const search = new URLSearchParams({
    limit: String(params.limit ?? 20),
    offset: String(params.offset ?? 0),
  });
  return useQuery({
    queryKey: ["idp", "bulk-jobs", params.limit ?? 20, params.offset ?? 0],
    queryFn: () => api.idpBulkJobs(search),
    // 実行中のジョブが 1 つでもあれば聞き直す
    refetchInterval: (q) =>
      (q.state.data?.items ?? []).some((j) => j.status === BULK_RUNNING)
        ? BULK_POLL_MS
        : false,
  });
}

/** 取込 1 件。**`running` の間だけ 2 秒ごとに聞き直す**(R7b fix1 中2)。 */
export function useIdpBulkJob(id: number | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "bulk-job", id],
    queryFn: () => api.idpBulkJob(id!),
    enabled: id !== null,
    refetchInterval: (q) =>
      q.state.data?.status === BULK_RUNNING ? BULK_POLL_MS : false,
  });
}

export function useIdpDomain(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: ["idp", "domain"], queryFn: api.idpDomain, enabled });
}

export function useIdpApiClients(params: { limit?: number; offset?: number }) {
  const api = useApi();
  const search = new URLSearchParams({
    limit: String(params.limit ?? 50),
    offset: String(params.offset ?? 0),
  });
  return useQuery({
    queryKey: ["idp", "api-clients", params.limit ?? 50, params.offset ?? 0],
    queryFn: () => api.idpApiClients(search),
  });
}

export function useIdpApiClient(id: number | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["idp", "api-client", id],
    queryFn: () => api.idpApiClient(id!),
    enabled: !!id,
  });
}
